import React, { Component } from 'react';
import { Formik, FieldArray } from 'formik';
import { TextInputField, Button, SelectField, Checkbox, Pane, Heading } from 'evergreen-ui';
import ProductPicker from '../../products/components/ProductPicker';
import ProductListCard from '../../products/components/ProductListCard';
import CardPicker from './CardPicker';
import CardListCard from './CardListCard';

import { arrayMoveImmutable as arrayMove } from 'array-move';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';

// Products and singles live in one ordered list so singles can sit
// among products. Each entry carries `kind` to pick its renderer; the
// shared position sequence is the entry's index, assigned on submit.
const SortableItem = SortableElement((props) =>
    <Pane display="inline-block" cursor="pointer">
        {props.value.kind === 'card'
            ? <CardListCard card={props.value} onDelete={props.onRemove} />
            : <ProductListCard product={props.value} onDelete={props.onRemove} />}
    </Pane>
)

const SortableList = SortableContainer((props) => {
    const items = props.items;

    return (
        <Pane display="flex" flexDirection="column" flexWrap="wrap">
            {items.map((value, index) => (
                <SortableItem onRemove={() => { props.onRemove(index) }}
                    key={`list-item-${value.kind}-${value.id}`}
                    index={index}
                    value={value} />
            ))}
        </Pane>
    )
})

const positionOf = (entry, throughKey) =>
    entry[throughKey] && entry[throughKey].position != null
        ? entry[throughKey].position
        : (entry.position != null ? entry.position : 0)

// Merge the separately-loaded products/cards into one list ordered by
// the shared position sequence (getOne returns each ordered within its
// own type; the through-row position is what interleaves them).
const buildItems = (initialValues) => {
    const products = (initialValues.products || []).map(p => ({ kind: 'product', ...p, position: positionOf(p, 'ProductListProduct') }));
    const cards = (initialValues.cards || []).map(c => ({ kind: 'card', ...c, position: positionOf(c, 'ProductListCard') }));
    return [...products, ...cards].sort((a, b) => a.position - b.position);
}

export default class ProductListForm extends Component {
    render() {
        const initialValues = { ...this.props.initialValues, items: buildItems(this.props.initialValues) };

        return (
            <Formik initialValues={initialValues}
                enableReinitialize={true}
                validationSchema={this.props.validationSchema}
                validateOnChange={false}
                validateOnBlur={false}
                onSubmit={(values) => {
                    const products = [];
                    const cards = [];
                    (values.items || []).forEach((item, index) => {
                        const { kind, ...entity } = item;
                        if (kind === 'card') {
                            cards.push({ ...entity, position: index });
                        } else {
                            products.push({ ...entity, position: index });
                        }
                    });

                    this.props.onSave({ ...values, products, cards });
                }}
            >
                {({ errors, setFieldValue, handleChange, handleBlur, handleSubmit, values }) => {
                    errors = Object.assign({}, this.props.errors, errors);

                    const handleSortEnd = ({ oldIndex, newIndex }) => {
                        setFieldValue('items', arrayMove((values.items || []), oldIndex, newIndex));
                    }

                    return <form>
                        <TextInputField
                            required
                            label="Nume"
                            name="name"
                            description="Numele listei de produse"
                            value={values.name || ""}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            validationMessage={errors.name} />
                        <TextInputField
                            required
                            label="SLUG"
                            name="slug"
                            description="SLUG lista de produse"
                            value={values.slug || ""}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            validationMessage={errors.slug} />

                        <TextInputField
                            required
                            label="Pozitie"
                            name="position"
                            description="Pozitia listei"
                            value={values.position || ""}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            validationMessage={errors.position} />

                        <SelectField
                            label="Target"
                            name="target"
                            description="Unde apare lista"
                            value={values.target}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        >
                            <option value="none">Nu are target</option>
                            <option value="homepage">Pagina Principala</option>
                        </SelectField>

                        <SelectField
                            label="Tip"
                            name="type"
                            description="Tipul listei"
                            value={values.type}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        >
                            <option value="grid">Grid</option>
                            <option value="spotlight">Spotlight</option>
                        </SelectField>

                        <Checkbox
                            name="active"
                            label="Activa"
                            checked={values.active || false}
                            onChange={handleChange} />

                        <Checkbox
                            name="hasMore"
                            label="Are buton de Mai Mult"
                            checked={values.hasMore || false}
                            onChange={handleChange} />


                        <TextInputField
                            required
                            label="URL Vizibil"
                            name="urlHref"
                            description="URL-ul vizibil"
                            value={values.urlHref}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            validationMessage={errors.urlHref} />

                        <TextInputField
                            required
                            label="URL Intern"
                            name="urlTarget"
                            description="URL-ul ruta interna (always ask Teo before changing)"
                            value={values.urlTarget}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            validationMessage={errors.urlTarget} />

                        <Pane marginBottom={16}>
                            <Pane borderBottom="default" paddingBottom={8} marginBottom={8}>
                                <Heading size={500}>Produse si Singles in Lista</Heading>
                            </Pane>
                            <FieldArray name="items" render={arrayHelpers => (
                                <>
                                    <Pane display="flex" flexWrap="wrap" marginBottom={8}>
                                        <Pane marginRight={8} marginBottom={8}>
                                            <ProductPicker minimal={true} single={false}
                                                onSubmit={(products) => products.forEach(product => arrayHelpers.push({ kind: 'product', ...product }))} />
                                        </Pane>
                                        <Pane marginBottom={8}>
                                            <CardPicker
                                                onSubmit={(cards) => cards.forEach(card => arrayHelpers.push({ kind: 'card', ...card }))} />
                                        </Pane>
                                    </Pane>

                                    <SortableList axis="y" items={values.items || []}
                                        onSortEnd={handleSortEnd}
                                        onRemove={arrayHelpers.remove}
                                    />
                                </>
                            )} />
                        </Pane>

                        <Pane borderTop="default" marginTop={16} paddingTop={16}>
                            <Button type="submit" onClick={handleSubmit}>
                                Salveaza
                            </Button>
                        </Pane>
                    </form>
                }}
            </Formik>
        );
    }
}
